import React from "react";
import Loading from "../../components/common/loading";
import ModalManager from "../../components/common/modal/ModalManager";
import axios from "axios";
import TextInput from "../../components/common/inputs/textInput";
import { formatDistance } from "date-fns";

// import NotificationManager from '../../components/common/modal/NotificationManager'
import NotificationManager from "../../utils/notificationManager";

import "./apiAccess.css";

const BaseModal = ({
  isOpen,
  title,
  subtitle,
  children,
  onClose,
  primaryAction,
  secondaryAction,
}) => {
  if (!isOpen) {
    return null;
  }

  return (
    <div className="api-access__modal-overlay" role="dialog" aria-modal="true">
      <div className="api-access__modal">
        <div className="api-access__modal-header">
          <div>
            <h2 className="api-access__modal-title">{title}</h2>
            {subtitle && <p className="api-access__modal-subtitle">{subtitle}</p>}
          </div>
          <button
            type="button"
            className="api-access__modal-close"
            aria-label="Close modal"
            onClick={onClose}
          >
            &times;
          </button>
        </div>
        <div className="api-access__modal-body">{children}</div>
        {(secondaryAction || primaryAction) && (
          <div className="api-access__modal-footer">
            {secondaryAction && (
              <button
                type="button"
                className="api-access__modal-button api-access__modal-button--secondary"
                onClick={secondaryAction.onClick}
                disabled={secondaryAction.disabled}
              >
                {secondaryAction.label}
              </button>
            )}
            {primaryAction && (
              <button
                type="button"
                className="api-access__modal-button api-access__modal-button--primary"
                onClick={primaryAction.onClick}
                disabled={primaryAction.disabled}
              >
                {primaryAction.label}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default class UserApiAccess extends React.Component {
  _isMounted = false;

  state = {
    tokens: [],
    tokensLoading: false,
    createModalOpen: false,
    creatingToken: false,
    createDescription: "",
    createError: "",
    showTokenModal: null,
  };

  componentDidMount() {
    this._isMounted = true;
    this.maybeFetchTokens(this.props.user);
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.user?.user_id &&
      this.props.user?.user_id !== prevProps.user?.user_id
    ) {
      this.maybeFetchTokens(this.props.user);
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  maybeFetchTokens = async (user) => {
    if (!user?.user_id) {
      return;
    }

    this.setState({ tokensLoading: true });

    try {
      const response = await axios.get(`/v1/users/${user.user_id}/tokens`, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
        },
      });
      const tokens = Array.isArray(response?.data?.data)
        ? response.data.data
        : [];
      if (this._isMounted) {
        this.setState({ tokens });
      }
    } catch (error) {
      NotificationManager.alert("Unable to load personal access tokens.");
    } finally {
      if (this._isMounted) {
        this.setState({ tokensLoading: false });
      }
    }
  };

  openCreateModal = () => {
    this.setState({
      createModalOpen: true,
      createDescription: "",
      createError: "",
    });
  };

  closeCreateModal = () => {
    if (this.state.creatingToken) {
      return;
    }

    this.setState({
      createModalOpen: false,
      createDescription: "",
      createError: "",
    });
  };

  createToken = async () => {
    const description = (this.state.createDescription || "").trim();

    if (!description) {
      this.setState({ createError: "Please provide a description for the token." });
      return;
    }

    if (!this.props.user?.user_id) {
      NotificationManager.alert("You need to be logged in to create a token.");
      return;
    }

    this.setState({ creatingToken: true, createError: "" });

    try {
      const response = await axios.post(
        `/v1/users/${this.props.user.user_id}/tokens`,
        {
          name: description,
        },
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        }
      );

      const createdToken = response?.data?.data;

      if (createdToken) {
        this.setState((prevState) => ({
          tokens: [...prevState.tokens, createdToken],
          createModalOpen: false,
          creatingToken: false,
          createDescription: "",
          showTokenModal: {
            name: createdToken.name,
            value: createdToken.token || "",
          },
        }));
        NotificationManager.success("Token created", "Success");
      } else {
        this.setState({ creatingToken: false });
        NotificationManager.alert("Unable to create the token.");
      }
    } catch (error) {
      this.setState({
        creatingToken: false,
        createError: "Something went wrong while creating the token.",
      });
      NotificationManager.alert("Error creating token", "Error");
    }
  };

  closeTokenModal = () => {
    this.setState({ showTokenModal: null });
  };

  removeToken = (id) => {
    ModalManager.confirm(
      `Are you sure you want to permanently delete this Personal Access Token?`,
      "Delete Personal Access Token",
      "Delete",
      (submitted, close) => {
        if (!submitted) {
          return;
        }

        axios
          .delete(`/v1/users/${this.props.user.user_id}/tokens/${id}`, {
            headers: {
              Authorization: "Bearer " + localStorage.getItem("token"),
            },
          })
          .then(() => {
            this.setState({
              tokens: this.state.tokens.filter((token) => token.id !== id),
            });
            NotificationManager.success("Token removed", "Success");
          })
          .catch(() => {
            NotificationManager.alert("Error removing token", "Error");
          });

        close();
      }
    );
  };

  toggleToken = (id) => {
    const token = this.state.tokens.find((item) => item.id === id);
    if (!token) {
      return;
    }

    axios
      .put(
        `/v1/users/${this.props.user.user_id}/tokens/${id}`,
        {
          is_active: !token.is_active,
        },
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        }
      )
      .then(() => {
        this.setState((prevState) => ({
          tokens: prevState.tokens.map((item) =>
            item.id === id ? { ...item, is_active: !item.is_active } : item
          ),
        }));
        NotificationManager.success("Token updated", "Success");
      })
      .catch(() => {
        NotificationManager.alert("Error updating token", "Error");
      });
  };

  handleCopy = (value) => {
    if (!value) {
      NotificationManager.alert("Nothing to copy.");
      return;
    }

    const copySuccess = () =>
      NotificationManager.success("Token copied to clipboard.");

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(value).then(copySuccess).catch(() => {
        this.copyFallback(value);
      });
      return;
    }

    this.copyFallback(value);
  };

  copyFallback(value) {
    try {
      const textArea = document.createElement("textarea");
      textArea.value = value;
      textArea.setAttribute("readonly", "");
      textArea.style.position = "absolute";
      textArea.style.left = "-9999px";
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      NotificationManager.success("Token copied to clipboard.");
    } catch (error) {
      NotificationManager.alert("Unable to copy token.");
    }
  }

  renderCreateTokenModal() {
    const { createModalOpen, creatingToken, createDescription, createError } =
      this.state;

    return (
      <BaseModal
        isOpen={createModalOpen}
        title="Create a Personal Access Token"
        subtitle="Give your token a short description so you remember what it's used for."
        onClose={this.closeCreateModal}
        secondaryAction={{
          label: "Cancel",
          onClick: this.closeCreateModal,
          disabled: creatingToken,
        }}
        primaryAction={{
          label: creatingToken ? "Creating..." : "Create token",
          onClick: this.createToken,
          disabled: creatingToken,
        }}
      >
        <div className="api-access__field">
          <label htmlFor="token-description">Token description</label>
          <input
            id="token-description"
            type="text"
            placeholder="e.g. CI automation"
            value={createDescription}
            onChange={(event) =>
              this.setState({ createDescription: event.target.value, createError: "" })
            }
            autoFocus
          />
        </div>
        {createError && <p className="api-access__error">{createError}</p>}
      </BaseModal>
    );
  }

  renderTokenValueModal() {
    const { showTokenModal } = this.state;

    if (!showTokenModal) {
      return null;
    }

    return (
      <BaseModal
        isOpen
        title="Personal Access Token"
        subtitle="Copy this token now. For security reasons, you won't be able to view it again."
        onClose={this.closeTokenModal}
        primaryAction={{
          label: "Done",
          onClick: this.closeTokenModal,
        }}
      >
        <p className="api-access__token-warning">
          <strong>{showTokenModal.name}</strong> was just created.
        </p>
        <div className="api-access__token-display">
          <div className="api-access__token-value">{showTokenModal.value}</div>
          <button
            type="button"
            className="api-access__copy-button"
            onClick={() => this.handleCopy(showTokenModal.value)}
            disabled={!showTokenModal.value}
          >
            Copy token
          </button>
        </div>
      </BaseModal>
    );
  }

  renderTokens() {
    const { tokens, tokensLoading } = this.state;

    if (tokensLoading) {
      return (
        <div style={{ display: "flex", justifyContent: "center", padding: "2rem 0" }}>
          <Loading />
        </div>
      );
    }

    if (!tokens.length) {
      return (
        <div className="api-access__empty">
          You don't have any personal access tokens yet. Create one to start using
          the API.
        </div>
      );
    }

    return (
      <div className="api-access__token-list">
        {tokens.map((token) => {
          const createdAtLabel = token.created_at
            ? `${formatDistance(new Date(token.created_at), new Date())} ago`
            : "Unknown";

          return (
            <div key={token.id} className="api-access__token-card">
              <div className="api-access__token-top">
                <p className="api-access__token-name">{token.name}</p>
                <span
                  className={`api-access__status${
                    token.is_active ? "" : " api-access__status--disabled"
                  }`}
                >
                  {token.is_active ? "Active" : "Disabled"}
                </span>
              </div>
              <div className="api-access__token-actions">
                <button
                  type="button"
                  className="api-access__action-button"
                  onClick={() => this.toggleToken(token.id)}
                >
                  {token.is_active ? "Disable" : "Enable"}
                </button>
                <button
                  type="button"
                  className="api-access__action-button api-access__action-button--danger"
                  onClick={() => this.removeToken(token.id)}
                >
                  Delete
                </button>
              </div>
              <div className="api-access__meta">
                <strong>Created:</strong> {createdAtLabel}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  render() {
    return (
      <div className="grid-container">
        <div className="api-access">
          <div className="api-access__layout">
            <section className="api-access__intro">
              <h2 className="api-access__heading">Security</h2>
              <p className="api-access__description">
                To interact with the HTTP API, include a Personal Access Token (PAT) in
                your requests for authentication. Generate tokens for tools or
                automations that need to access your SynCloud resources.
              </p>
              <div className="api-access__code-block">
                <code>{`curl --request GET \\
  --url ${window.location.origin}/v1/devices \\
  --header 'accept: application/json' \\
  --header 'Authorization: Bearer COPY_PASTED_TOKEN_HERE'`}</code>
              </div>
            </section>
            <section className="api-access__tokens">
              <div className="api-access__token-header">
                <div>
                  <h3 className="api-access__section-title">Personal Access Tokens</h3>
                  <p className="api-access__section-subtitle">
                    Manage access tokens that let external systems talk to the SynCloud
                    API on your behalf.
                  </p>
                </div>
                <button
                  type="button"
                  className="api-access__primary-button"
                  onClick={this.openCreateModal}
                >
                  Create token
                </button>
              </div>
              {this.renderTokens()}
            </section>
          </div>
        </div>
        {this.renderCreateTokenModal()}
        {this.renderTokenValueModal()}
      </div>
    );
  }
}

class ChangePasswordForm extends React.Component {
  state = {
    password: "",
    confirmPassword: "",
    currentPassword: "",
    errorMsg: "",
    loading: false,
  };
  resetPasswordButtonPress = () => {
    this.setState({
      errorMsg: "",
      loading: true,
    });
    if (this.state.password === this.state.confirmPassword) {
      this.props.resetPassword(
        {
          currentPass: this.state.currentPassword,
          password: this.state.password,
        },
        (err, res) => {
          this.setState({
            loading: false,
          });
          if (err) {
            this.setState({
              errorMsg: err.message,
            });
          } else {
            if (res.data) {
              this.props.close();
              NotificationManager.success("User Password Changed Successfully");
            }
          }
        }
      );
    } else {
      this.setState({
        loading: false,
        errorMsg: "Passwords do not match",
      });
    }
  };

  render() {
    return (
      <React.Fragment>
        <TextInput
          label="Current Password"
          callback={(text) => this.setState({ currentPassword: text })}
          value={this.state.currentPassword}
          inputContainerStyle={{
            width: "100%",
            marginBottom: "0px",
          }}
          type="password"
        />
        <TextInput
          label="Password"
          callback={(text) => this.setState({ password: text })}
          value={this.state.password}
          inputContainerStyle={{
            width: "100%",
            marginBottom: "0px",
          }}
          type="password"
        />
        <TextInput
          label="Confirm Password"
          callback={(text) => this.setState({ confirmPassword: text })}
          value={this.state.confirmPassword}
          inputContainerStyle={{
            width: "100%",
            marginBottom: "0px",
          }}
          type="password"
        />
        <div
          style={{
            color: "maroon",
            padding: "10px 0px 20px 0px",
          }}
        >
          <div>
            {[
              {
                name: "2 lowercase characters required",
                valid: /[a-z].*[a-z]/.test(this.state.password),
              },
              {
                name: "2 uppercase characters required",
                valid: /[A-Z].*[A-Z]/.test(this.state.password),
              },
              {
                name: "2 number characters required",
                valid: /[0-9].*[0-9]/.test(this.state.password),
              },
              {
                name: "2 special characters required",
                valid: /[!@#$%^&*;].*[!@#$%^&*;]/.test(this.state.password),
              },
            ].map((validation) => {
              return (
                <div
                  key={validation.name}
                  style={{
                    color: validation.valid ? "forestgreen" : "#666",
                    display: "flex",
                    paddingBottom: "10px",
                    alignItems: "center",
                  }}
                >
                  <i className="material-icons">
                    {validation.valid ? "check" : "cancel"}
                  </i>
                  <span>{validation.name}</span>
                </div>
              );
            })}
          </div>
          {this.state.errorMsg &&
            this.state.errorMsg.split(",").map((msg) => {
              return (
                <div key={msg}>
                  <div
                    style={{
                      color: "maroon",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <i
                      className="material-icons"
                      style={{ marginRight: "5px" }}
                    >
                      error
                    </i>
                    <span>{msg}</span>
                  </div>
                </div>
              );
            })}
        </div>
        <button
          className="button"
          onClick={() => {
            this.resetPasswordButtonPress();
          }}
        >
          {this.state.loading ? <Loading /> : "Reset Password"}
        </button>
      </React.Fragment>
    );
  }
}
